import Typography from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CarouselImage, Image, Promotion, AssetType } from '@/domains';
import { createCarouselImage, deleteCarouselImage, getCarouselImages } from '@/services/api/carousel';
import { createPromotion, deletePromotion, getPromotions, updatePromotion } from '@/services/api/promotion';
import { uploadImage } from '@/services/api/media';
import { generateImageId } from '@/util';
import { EllipsisIcon, PencilIcon, XIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import FileUpload from '@/components/FileUpload';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

import { PromotionForm } from './PromotionForm';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const readAsDataUrl = (file: File): Promise<Image> =>
  new Promise<Image>((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      resolve({ id: generateImageId('l'), url: reader.result as string, thumbnailUrl: '', file });
    });
  });

const Platform: React.FC = () => {
  const [editPromotion, setEditPromotion] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [carouselImages, setCarouselImages] = useState<CarouselImage[]>([]);
  const [uploading, setUploading] = useState(false);

  const loadPromotions = async () => {
    const promotions = await getPromotions();
    setPromotions(promotions);
  };

  const loadCarouselImages = async () => {
    try {
      const images = await getCarouselImages();
      setCarouselImages(images);
    } catch (error) {
      console.error('Failed to load carousel images:', error);
    }
  };

  const handleImageUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setUploading(true);
    try {
      const file = files[0];
      const image = await readAsDataUrl(file);
      const { imageUrl, thumbnailUrl } = await uploadImage(image, AssetType.CarouselImage);

      await createCarouselImage({ imageUrl, thumbnailUrl });
      await loadCarouselImages();
      toast.success('Carousel image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload carousel image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCarouselImage = async (id: string) => {
    try {
      await deleteCarouselImage(id);
      await loadCarouselImages();
      toast.success('Carousel image deleted successfully');
    } catch (error) {
      toast.error('Failed to delete carousel image');
      console.error('Delete error:', error);
    }
  };

  useEffect(() => {
    loadPromotions();
    loadCarouselImages();
  }, []);

  return (
    <React.Fragment>
      <div className="flex mb-4">
        <Typography is="h1" size="2xl" weight="bold">
          Platform
        </Typography>
      </div>

      <div className="mb-8">
        <div className="flex justify-between mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Carousel Images</h3>
          <FileUpload
            id="carousel-image-upload"
            accept="image/png,image/jpeg"
            onChange={handleImageUpload}
            disabled={uploading}
          >
            <Button variant="outline" disabled={uploading} asChild>
              <div>{uploading ? 'Uploading...' : 'Upload Image'}</div>
            </Button>
          </FileUpload>
        </div>

        {carouselImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {carouselImages.map((image) => (
              <div key={image.id} className="relative group">
                <img
                  src={image.imageUrl}
                  alt="Carousel"
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDeleteCarouselImage(image.id)}
                >
                  <XIcon className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No carousel images yet. Upload an image to get started.</div>
        )}
      </div>

      <div>
        <div className="flex justify-between">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Promotions</h3>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">Add new</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editPromotion ? 'Edit' : 'Add'} promotion</DialogTitle>
              </DialogHeader>
              <PromotionForm promotion={editPromotion} onSave={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rule</TableHead>
              <TableHead>Promotion (%)</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {promotions.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{item.days} days</TableCell>
                <TableCell>{item.discountPercentage}% off</TableCell>
                <TableCell className="float-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        <EllipsisIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={() => {
                          setEditPromotion(item);
                          setDialogOpen(true);
                        }}
                      >
                        <PencilIcon /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <XIcon /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </React.Fragment>
  );
};

export default Platform;
